import { Link } from '@inertiajs/vue3';
import { h, isVNode, VNode } from 'vue';

type Descriptor =
    | string
    | number
    | VNode
    | Array<Descriptor>
    | {
          component: string | any;
          props?: Record<string, any>;
          children?: Descriptor | Array<Descriptor>;
      };

const componentRegistry: Record<string, any> = {
    Link,
};

function resolveComponent(component: string | any) {
    if (typeof component === 'string') {
        return componentRegistry[component] || component;
    }
    return component;
}

export function renderNode(node: Descriptor | null | undefined): any {
    if (node == null) return null;
    if (typeof node === 'string') {
        // Render basic HTML in strings (e.g., <b>, <i>, <u>, <br>, etc.)
        // Fallback to plain text when no HTML tags are present
        const containsHtml = /<\/?[a-z][\s\S]*>/i.test(node);
        return containsHtml ? h('span', { innerHTML: node }) : node;
    }
    if (typeof node === 'number') return node;
    if (isVNode(node)) return node;
    if (Array.isArray(node)) return node.map((n) => renderNode(n));
    if (typeof node === 'object' && (node as any).component) {
        const n = node as Exclude<Descriptor, string | number | VNode | Array<Descriptor>>;
        const Comp = resolveComponent((n as any).component);
        const props = (n as any).props || {};
        let children: any = undefined;
        if (Array.isArray((n as any).children)) {
            children = ((n as any).children as Array<Descriptor>).map((c) => renderNode(c));
        } else if ((n as any).children != null) {
            children = renderNode((n as any).children as Descriptor);
        }
        return h(Comp, props, children);
    }
    try {
        return JSON.stringify(node as any);
    } catch {
        return String(node);
    }
}

function toCamelCase(name: string): string {
    return name.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}

function parseAttributePairs(attrsString: string): Record<string, any> {
    const attrs: Record<string, any> = {};
    if (!attrsString) return attrs;
    const attrRe = /([:@\w-]+)(?:=(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
    let m: RegExpExecArray | null;
    while ((m = attrRe.exec(attrsString)) !== null) {
        const rawName = m[1];
        const value = m[2] ?? m[3] ?? m[4] ?? true;
        attrs[rawName] = value;
    }
    return attrs;
}

function coerceBoolean(value: any): boolean {
    if (value === true) return true;
    if (typeof value !== 'string') return Boolean(value);
    const v = value.toLowerCase();
    if (v === 'true' || v === '1' || v === '') return true;
    if (v === 'false' || v === '0' || v === 'null' || v === 'undefined') return false;
    return Boolean(value);
}

// Backward-compat for plain string messages that contain <Link ...>...</Link>
export function parseLinksFromString(message: unknown): Array<any> | null {
    if (typeof message !== 'string' || !message.includes('<Link')) return null;
    const re = /<Link\b([^>]*)>([\s\S]*?)<\/Link>/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    const nodes: Array<any> = [];
    while ((match = re.exec(message)) !== null) {
        const start = match.index;
        const end = re.lastIndex;
        if (start > lastIndex) {
            nodes.push(message.slice(lastIndex, start));
        }
        const attrsString = match[1] || '';
        const inner = match[2] || '';
        const rawAttrs = parseAttributePairs(attrsString);
        const props: Record<string, any> = {};
        Object.keys(rawAttrs).forEach((key) => {
            if (key === 'class') {
                props.class = rawAttrs[key];
            } else if (key === 'preserve-state') {
                props.preserveState = coerceBoolean(rawAttrs[key]);
            } else if (key === 'preserve-scroll') {
                props.preserveScroll = coerceBoolean(rawAttrs[key]);
            } else if (key === 'replace') {
                props.replace = coerceBoolean(rawAttrs[key]);
            } else {
                props[toCamelCase(key)] = rawAttrs[key];
            }
        });
        nodes.push(h(Link, props, () => inner));
        lastIndex = end;
    }
    if (nodes.length === 0) return null;
    if (lastIndex < message.length) {
        nodes.push(message.slice(lastIndex));
    }
    return nodes;
}
